import React from 'react';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import SEO from './SEO';
import ContactForm from './ContactForm';
import Footer from './Footer';
import { faqSchema, breadcrumbSchema } from '../utils/schemas';

const BlogArticleTemplate = ({
  title,
  description,
  path,
  author = 'Manage Your Loans Team',
  date,
  updated,
  readTime,
  heroImage,
  children,
  faqs,
}) => {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    author: {
      '@type': 'Organization',
      name: author,
      url: 'https://manageyourloans.com.au',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Manage Your Loans',
      url: 'https://manageyourloans.com.au',
      logo: {
        '@type': 'ImageObject',
        url: 'https://manageyourloans.com.au/logo.png',
      },
    },
    datePublished: date,
    dateModified: updated || date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://manageyourloans.com.au${path}`,
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={title}
        description={description}
        path={path}
        schema={[
          articleSchema,
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: title, url: path },
          ]),
          ...(faqs
            ? [faqSchema(faqs.map((f) => ({ question: f.q, answer: f.a })))]
            : []),
        ]}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-[#99DF63] hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Blog</span>
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {title}
            </h1>
            <div className="flex flex-wrap items-center justify-center gap-6 text-gray-300 text-sm">
              <span className="inline-flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>{author}</span>
              </span>
              <span className="inline-flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{updated ? `Updated ${updated}` : date}</span>
              </span>
              <span className="inline-flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{readTime} min read</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto article-content">
            {children}
          </div>
        </div>
      </section>

      {/* General Advice Warning */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white border border-amber-200 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-800">General Advice Warning:</strong> The
              information provided in this article is general in nature and does not take
              into account your objectives, financial situation or needs. You should
              consider whether it is appropriate for you before acting on it. Credit
              Manage Your Loans Pty Ltd ABN 34 652 442 790 is a Credit Representative (540988) of Home Mortgage Plus Pty Ltd ACN 105 991 839 (ACL 392200).
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00594C] text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details
                  key={i}
                  className="bg-white rounded-xl border border-gray-100 group"
                >
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-[#00594C] hover:text-[#99DF63] transition-colors list-none flex items-center justify-between">
                    {faq.q}
                    <svg
                      className="w-5 h-5 transition-transform group-open:rotate-180 flex-shrink-0 ml-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactForm pageContext="blog" />
      <Footer />
    </div>
  );
};

export default BlogArticleTemplate;
